import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AppInput from "../../components/Core/AppInput/AppInput";
import Checkbox from "../../components/Core/Checkbox/Checkbox";
import Flex from "../../components/Layout/Flex";
import { DenialReason } from "../../models/Organisation";

/** Reserved key for the free-text "Other" reason. Mirrors the job-service handler. */
export const OTHER_REASON_KEY = "OTHER";

interface DenyReasonModalBodyProps {
	reasons: DenialReason[];
	/** Whether the free-text "Annat" (OTHER) fallback option is offered. */
	allowOther?: boolean;
	/** Called whenever the selection changes so the modal's onAccept can read it. */
	onChange: (value: { reasonKey?: string; reasonText?: string }) => void;
}

/**
 * Body rendered inside the deny-job confirm modal when the org has configured
 * denial reasons. Renders the reasons as a single-select checkbox list (only one
 * reason at a time); when the org enabled it, the last "Annat" (OTHER) option
 * reveals a free-text input. Defaults to the first configured reason so a valid
 * value is always submitted. Not rendered at all when the org has no reasons
 * configured.
 */
const DenyReasonModalBody: React.FC<DenyReasonModalBodyProps> = ({
	reasons,
	allowOther,
	onChange,
}) => {
	const { t } = useTranslation();

	const [reasonKey, setReasonKey] = useState<string>(
		reasons[0]?.key ?? (allowOther ? OTHER_REASON_KEY : "")
	);
	const [reasonText, setReasonText] = useState<string>("");

	useEffect(() => {
		onChange({
			reasonKey,
			reasonText: reasonKey === OTHER_REASON_KEY ? reasonText : undefined,
		});
	}, [reasonKey, reasonText, onChange]);

	return (
		<Flex direction="column" margin="1rem 0 0 0">
			{reasons.map(({ key, name }) => (
				<Checkbox
					key={key}
					label={name.sv}
					isChecked={reasonKey === key}
					onChange={() => setReasonKey(key)}
					margin="0 0 0.75rem 0"
				/>
			))}

			{allowOther && (
				<Checkbox
					label={t("DENY_JOB_REASON_OTHER")}
					isChecked={reasonKey === OTHER_REASON_KEY}
					onChange={() => setReasonKey(OTHER_REASON_KEY)}
					margin="0 0 0.75rem 0"
				/>
			)}

			{reasonKey === OTHER_REASON_KEY && (
				<AppInput
					margin="0.25rem 0 0 0"
					value={reasonText}
					onChangeText={setReasonText}
					placeholder={t("DENY_JOB_REASON_OTHER_PLACEHOLDER")}
				/>
			)}
		</Flex>
	);
};

export default DenyReasonModalBody;
